import { useEffect, useMemo, useState } from 'react';
import './AddToHomeScreenHint.css';

const DISMISS_KEY = 'colmeia_portal_a2hs_dismissed';

const isStandalone = () =>
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

const getPlatform = () => {
    const ua = window.navigator.userAgent || '';
    if (/iphone|ipad|ipod/i.test(ua) && !window.MSStream) return 'ios';
    if (/android/i.test(ua)) return 'android';
    return 'other';
};

/**
 * Aviso discreto e dispensável ensinando o colaborador a salvar o portal na tela inicial.
 * - Quando o navegador oferece instalação (beforeinstallprompt), mostra o botão "Instalar".
 * - iPhone/Safari: instrução manual (Compartilhar → Adicionar à Tela de Início).
 * - Android sem prompt (sem service worker): instrução manual (menu ⋮ → Adicionar à tela inicial).
 * - Some se já está instalado (standalone) ou se o usuário já dispensou.
 */
const AddToHomeScreenHint = () => {
    const [installEvent, setInstallEvent] = useState(null);
    const [visible, setVisible] = useState(false);
    const platform = useMemo(getPlatform, []);

    useEffect(() => {
        if (isStandalone() || localStorage.getItem(DISMISS_KEY) === 'true') return undefined;

        // No mobile mostramos sempre (com instrução manual); no desktop, só se houver prompt nativo.
        if (platform === 'ios' || platform === 'android') {
            setVisible(true);
        }

        const onBeforeInstall = (event) => {
            event.preventDefault();
            setInstallEvent(event);
            setVisible(true);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstall);

        const onInstalled = () => {
            localStorage.setItem(DISMISS_KEY, 'true');
            setVisible(false);
        };
        window.addEventListener('appinstalled', onInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, [platform]);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, 'true');
        setVisible(false);
    };

    const install = async () => {
        if (!installEvent) return;
        installEvent.prompt();
        try {
            await installEvent.userChoice;
        } catch {
            // usuário fechou o diálogo; nada a fazer
        }
        setInstallEvent(null);
        dismiss();
    };

    if (!visible) return null;

    let instruction;
    if (!installEvent && platform === 'ios') {
        instruction = (
            <span>
                Toque em <i className="fa-solid fa-arrow-up-from-bracket"></i> Compartilhar e depois em
                “Adicionar à Tela de Início”.
            </span>
        );
    } else if (!installEvent && platform === 'android') {
        instruction = (
            <span>
                Toque no menu <i className="fa-solid fa-ellipsis-vertical"></i> do navegador e em
                “Adicionar à tela inicial”.
            </span>
        );
    } else {
        instruction = <span>Acesse sua agenda como um app, direto do ícone no celular.</span>;
    }

    return (
        <div className="a2hs-hint" role="region" aria-label="Adicionar à tela inicial">
            <i className="fa-solid fa-mobile-screen-button a2hs-hint__icon"></i>
            <div className="a2hs-hint__text">
                <strong>Adicione o portal à tela inicial</strong>
                {instruction}
            </div>
            {installEvent && (
                <button type="button" className="a2hs-hint__btn" onClick={install}>
                    Instalar
                </button>
            )}
            <button type="button" className="a2hs-hint__close" onClick={dismiss} aria-label="Dispensar">
                <i className="fa-solid fa-xmark"></i>
            </button>
        </div>
    );
};

export default AddToHomeScreenHint;
